/**
 * Référentiels / états intermédiaires (`10-parametrage-administration.md`).
 *
 * Un statut opérationnel exige une catégorie principale valide : le serveur refuse toute écriture
 * qui en serait dépourvue (`CONFLICT`), l'écran ne fait que le proposer.
 *
 * Le code d'une valeur (`en_service`, `totalenergies`…) est un identifiant technique interne, sans
 * intérêt pour l'administrateur qui gère le référentiel : il n'est jamais affiché ni saisi ici. À la
 * création, il est dérivé du libellé (`slugifier`) ; à la modification, chaque ligne porte son propre
 * bouton « Modifier » qui bascule ses cellules en édition, plutôt qu'un unique formulaire en tête de
 * page où il fallait retaper le code exact de la ligne visée pour la retrouver.
 *
 * `correspondance_tracker` reste à part : son code **est** le numéro de série du tracker, une donnée
 * métier à part entière, pas un identifiant technique — il continue d'être saisi et affiché.
 *
 * Pas de bouton de suppression définitive : les données historiques (fiches GRV, documents,
 * événements de fiche de vie…) référencent ces codes, et les supprimer les rendrait illisibles —
 * exactement le raisonnement déjà appliqué aux géozones (`docs/DECISIONS.md` D70) et aux règles
 * d'alerte. « Désactiver » reste donc la seule voie de retrait, réversible en modifiant la ligne.
 */
import { useState } from "react";

import { ErreurApi } from "../../shared/api/erreurs";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { formaterDate } from "../../shared/composants/formatage";
import type { ValeurReferentielPubliee } from "../../shared/api/types";
import {
  useDesactiverValeurReferentiel,
  useEnregistrerValeurReferentiel,
  useReferentiel,
} from "./requetes";

const TYPES: { valeur: string; libelle: string }[] = [
  { valeur: "statut_operationnel", libelle: "Statuts opérationnels" },
  { valeur: "type_intervention", libelle: "Types d'intervention" },
  { valeur: "type_document", libelle: "Types de document" },
  { valeur: "client", libelle: "Clients" },
  { valeur: "produit_transporte", libelle: "Produits transportés" },
  { valeur: "checklist_question", libelle: "Questions de check-list" },
  { valeur: "correspondance_tracker", libelle: "Correspondance tracker → GRV" },
];

// Doit rester aligné sur `kpi/models.py::CategoriePrincipale` : ce type énuméré est fixe côté
// backend (CDC §5, voir `docs/DECISIONS.md`) et toute valeur hors de cette liste est rejetée par
// `admin/service.py::_valider_statut_operationnel`.
const CATEGORIES_PRINCIPALES = [
  "plein",
  "vide",
  "en_transit",
  "en_attente_retour",
  "en_atelier",
  "sorti_du_parc",
  "a_confirmer",
];

// Deux des sept catégories sont aussi basculées automatiquement par le système, en plus de rester
// modifiables à la main (`docs/DECISIONS.md` D88, `grv/service.py::STATUTS_AUTOMATISABLES`) : la
// règle elle-même vit dans le code (ingestion, cycle planifié), pas dans ce référentiel — ce texte
// ne fait que la documenter là où un administrateur consulte les statuts possibles.
const STATUTS_AUTOMATIQUES: { code: string; regle: string }[] = [
  {
    code: "en_transit",
    regle:
      "posé dès qu'une position reçue place le GRV dans un corridor de transit ou hors de toute " +
      "géozone connue ; à l'arrivée dans une géozone réelle, le statut passe à « à confirmer » — " +
      "le système constate un déplacement, jamais si le GRV est plein ou vide à l'arrivée.",
  },
  {
    code: "sorti_du_parc",
    regle:
      "posé quand le tracker est resté silencieux bien plus longtemps que le seuil « hors réseau » " +
      "(seuil « Silence du tracker au-delà duquel un GRV bascule automatiquement sorti du parc », " +
      "réglable dans Paramétrage → Seuils).",
  },
];

function slugifier(texte: string): string {
  const base = texte
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "valeur";
}

function codeDisponible(souhaite: string, existants: ReadonlySet<string>): string {
  if (!existants.has(souhaite)) return souhaite;
  let compteur = 2;
  while (existants.has(`${souhaite}_${compteur}`)) compteur += 1;
  return `${souhaite}_${compteur}`;
}

export function EcranReferentiels() {
  const [type, definirType] = useState(TYPES[0]!.valeur);
  const requete = useReferentiel(type);
  const enregistrer = useEnregistrerValeurReferentiel(type);
  const desactiver = useDesactiverValeurReferentiel(type);

  const [libelle, definirLibelle] = useState("");
  const [categoriePrincipale, definirCategorie] = useState("");
  const [codeTracker, definirCodeTracker] = useState("");
  const [numeroGrv, definirNumeroGrv] = useState("");

  const [ligneEnEdition, definirLigneEnEdition] = useState<string | null>(null);
  const [brouillon, definirBrouillon] = useState({
    libelle: "",
    categoriePrincipale: "",
    numeroGrv: "",
  });

  const exigeCategorie = type === "statut_operationnel";
  const estCorrespondanceTracker = type === "correspondance_tracker";

  const valeurs = requete.data?.data ?? {};
  const codes = Object.keys(valeurs).sort();

  function changerType(nouveauType: string) {
    definirType(nouveauType);
    definirLigneEnEdition(null);
    definirLibelle("");
    definirCategorie("");
    definirCodeTracker("");
    definirNumeroGrv("");
  }

  async function creer(evenement: React.FormEvent) {
    evenement.preventDefault();
    const code = estCorrespondanceTracker
      ? codeTracker
      : codeDisponible(slugifier(libelle), new Set(codes));
    await enregistrer.mutateAsync({
      code,
      valeur: {
        // Le libellé n'a pas de sens propre pour une correspondance tracker : le code (numéro de
        // série) et le numéro de GRV suffisent à l'identifier, pas la peine de le faire saisir.
        libelle: estCorrespondanceTracker ? `Tracker ${codeTracker} → ${numeroGrv}` : libelle,
        actif: true,
        categorie_principale: exigeCategorie ? categoriePrincipale : null,
        numero_grv: estCorrespondanceTracker ? numeroGrv : null,
      },
    });
    definirLibelle("");
    definirCategorie("");
    definirCodeTracker("");
    definirNumeroGrv("");
  }

  function ouvrirEdition(code: string, valeur: ValeurReferentielPubliee) {
    definirBrouillon({
      libelle: valeur.libelle,
      categoriePrincipale: valeur.categorie_principale ?? "",
      numeroGrv: valeur.numero_grv ?? "",
    });
    definirLigneEnEdition(code);
  }

  async function enregistrerEdition(code: string, evenement: React.FormEvent) {
    evenement.preventDefault();
    await enregistrer.mutateAsync({
      code,
      valeur: {
        libelle: estCorrespondanceTracker ? `Tracker ${code} → ${brouillon.numeroGrv}` : brouillon.libelle,
        // Réenregistrer une ligne la réactive si elle était désactivée : c'est la seule façon de
        // revenir en arrière après une désactivation, il n'y a pas de bouton « Réactiver » séparé.
        actif: true,
        categorie_principale: exigeCategorie ? brouillon.categoriePrincipale : null,
        numero_grv: estCorrespondanceTracker ? brouillon.numeroGrv : null,
      },
    });
    definirLigneEnEdition(null);
  }

  return (
    <section>
      <h1>Référentiels</h1>

      <div className="barre-de-filtres">
        <label htmlFor="type-referentiel">Référentiel</label>
        <select id="type-referentiel" value={type} onChange={(e) => changerType(e.target.value)}>
          {TYPES.map((t) => (
            <option key={t.valeur} value={t.valeur}>
              {t.libelle}
            </option>
          ))}
        </select>
      </div>

      {estCorrespondanceTracker && (
        <p className="carte__description">
          Le code est le numéro de série du tracker (affiché « Serial Number » dans l'interface
          Positrex). Un changement de tracker sur un GRV se modifie ici, jamais dans Positrex : cette
          table garde l'historique et l'auteur de chaque changement (journal d'audit).
        </p>
      )}

      {exigeCategorie && (
        <div className="carte">
          <h3>Statuts automatiques</h3>
          <p className="carte__description">
            Les statuts ci-dessous restent modifiables à la main (onglet « Action » de la fiche
            GRV), mais le système les bascule aussi tout seul, dans les conditions suivantes. Tout
            autre statut — les cinq autres catégories, ou une valeur créée ici par un
            administrateur — n'est jamais modifié que manuellement.
          </p>
          <dl>
            {STATUTS_AUTOMATIQUES.map((statut) => (
              <div className="carte__champ" key={statut.code}>
                <dt>{statut.code}</dt>
                <dd>{statut.regle}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      <form className="formulaire-ligne" onSubmit={(e) => void creer(e)}>
        {estCorrespondanceTracker ? (
          <>
            <label htmlFor="code-tracker">Numéro de série tracker</label>
            <input
              id="code-tracker"
              required
              minLength={1}
              value={codeTracker}
              onChange={(e) => definirCodeTracker(e.target.value)}
            />
            <label htmlFor="numero-grv-valeur">Numéro de GRV</label>
            <input
              id="numero-grv-valeur"
              required
              minLength={1}
              value={numeroGrv}
              onChange={(e) => definirNumeroGrv(e.target.value)}
            />
          </>
        ) : (
          <>
            <label htmlFor="libelle-valeur">Libellé</label>
            <input
              id="libelle-valeur"
              required
              minLength={1}
              value={libelle}
              onChange={(e) => definirLibelle(e.target.value)}
            />
          </>
        )}
        {exigeCategorie && (
          <>
            <label htmlFor="categorie-principale">Catégorie principale</label>
            <select
              id="categorie-principale"
              required
              value={categoriePrincipale}
              onChange={(e) => definirCategorie(e.target.value)}
            >
              <option value="">—</option>
              {CATEGORIES_PRINCIPALES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </>
        )}
        <button type="submit" disabled={enregistrer.isPending}>
          Ajouter
        </button>
        {enregistrer.isError && (
          <span role="alert">
            {enregistrer.error instanceof ErreurApi
              ? enregistrer.error.message
              : "L'enregistrement a échoué."}
          </span>
        )}
      </form>

      {requete.isPending && <Chargement libelle="Chargement du référentiel…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}
      {requete.data && codes.length === 0 && <Vide libelle="Ce référentiel est vide." />}
      {requete.data && codes.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              {estCorrespondanceTracker ? (
                <th scope="col">Numéro de série tracker</th>
              ) : (
                <th scope="col">Libellé</th>
              )}
              {estCorrespondanceTracker && <th scope="col">Numéro de GRV</th>}
              {estCorrespondanceTracker && <th scope="col">Depuis</th>}
              {exigeCategorie && <th scope="col">Catégorie principale</th>}
              <th scope="col">Actif</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => {
              const valeur = valeurs[c]!;
              const enEdition = ligneEnEdition === c;

              if (enEdition) {
                return (
                  <tr key={c}>
                    <td>
                      {estCorrespondanceTracker ? (
                        c
                      ) : (
                        <input
                          aria-label="Libellé"
                          required
                          minLength={1}
                          value={brouillon.libelle}
                          onChange={(e) =>
                            definirBrouillon((b) => ({ ...b, libelle: e.target.value }))
                          }
                        />
                      )}
                    </td>
                    {estCorrespondanceTracker && (
                      <td>
                        <input
                          aria-label="Numéro de GRV"
                          required
                          minLength={1}
                          value={brouillon.numeroGrv}
                          onChange={(e) =>
                            definirBrouillon((b) => ({ ...b, numeroGrv: e.target.value }))
                          }
                        />
                      </td>
                    )}
                    {estCorrespondanceTracker && (
                      // Calculé côté serveur (`docs/DECISIONS.md` D73), jamais saisi : rien à
                      // éditer ici, juste le rappeler pendant qu'on modifie le reste de la ligne.
                      <td>{formaterDate(valeur.depuis)}</td>
                    )}
                    {exigeCategorie && (
                      <td>
                        <select
                          aria-label="Catégorie principale"
                          required
                          value={brouillon.categoriePrincipale}
                          onChange={(e) =>
                            definirBrouillon((b) => ({ ...b, categoriePrincipale: e.target.value }))
                          }
                        >
                          <option value="">—</option>
                          {CATEGORIES_PRINCIPALES.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td>{valeur.actif === false ? "non" : "oui"}</td>
                    <td>
                      <form
                        className="formulaire-actions"
                        onSubmit={(e) => void enregistrerEdition(c, e)}
                      >
                        <button type="submit" disabled={enregistrer.isPending}>
                          Enregistrer
                        </button>
                        <button type="button" onClick={() => definirLigneEnEdition(null)}>
                          Annuler
                        </button>
                      </form>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={c}>
                  <td>{estCorrespondanceTracker ? c : valeur.libelle}</td>
                  {estCorrespondanceTracker && <td>{valeur.numero_grv ?? "—"}</td>}
                  {estCorrespondanceTracker && <td>{formaterDate(valeur.depuis)}</td>}
                  {exigeCategorie && <td>{valeur.categorie_principale ?? "—"}</td>}
                  <td>{valeur.actif === false ? "non" : "oui"}</td>
                  <td className="formulaire-actions">
                    <button type="button" onClick={() => ouvrirEdition(c, valeur)}>
                      Modifier
                    </button>
                    {valeur.actif !== false && (
                      <button
                        type="button"
                        onClick={() => desactiver.mutate(c)}
                        disabled={desactiver.isPending}
                      >
                        Désactiver
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
